import Image, { type StaticImageData } from 'next/image'
import { GOLD, BORDER_GOLD } from '@/app/lib/portal/theme'

/** First and last initials: the monogram shown until a headshot arrives. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/)
  const last = words.length > 1 ? words[words.length - 1][0] : ''
  return `${words[0][0]}${last}`.toUpperCase()
}

/**
 * A 4:5 portrait: the headshot when we have one, otherwise a gold monogram in
 * the same frame, so a page can list everyone before every photo is in. No
 * hooks, so both the server pages and the client directory draw it. The
 * monogram is decorative; the name beside or below it is the label.
 */
export function Portrait({
  photo,
  name,
  credentials,
  sizes,
  initialsSize,
  framed = false,
}: {
  photo: StaticImageData | null
  name: string
  credentials: string | null
  sizes: string
  initialsSize: number
  /** Rounded corners and a gold hairline, for a portrait that stands alone. */
  framed?: boolean
}) {
  const frame = framed ? { borderRadius: 14, border: `1px solid ${BORDER_GOLD}` } : {}
  if (photo) {
    return (
      <Image
        src={photo}
        alt={credentials ? `${name}, ${credentials}` : name}
        sizes={sizes}
        className="block h-auto w-full"
        style={{ aspectRatio: '4 / 5', objectFit: 'cover', objectPosition: 'center top', ...frame }}
      />
    )
  }
  return (
    <div
      aria-hidden="true"
      className="grid w-full place-content-center"
      style={{
        aspectRatio: '4 / 5',
        background: 'linear-gradient(165deg, rgba(223,192,106,0.13) 0%, rgba(9,21,37,0.55) 72%)',
        ...frame,
      }}
    >
      <span style={{ fontFamily: 'Georgia, serif', fontSize: initialsSize, letterSpacing: '0.06em', color: GOLD, opacity: 0.88 }}>
        {initials(name)}
      </span>
    </div>
  )
}
